import { ReactNode } from 'react';

import { CompletionReward, MissionStatus } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { MissionCtaButton } from './mission-cta-button';
import { getProgressParams, isProgressBarType } from './utils';

interface MissionStatusBadgesProps {
  isExpired: boolean;
  isRejected: boolean;
}

export const MissionStatusBadges = ({ isExpired, isRejected }: MissionStatusBadgesProps) => {
  if (isExpired)
    return (
      <Badge type='tint' color='neutral'>
        <FormattedMessage id='Reward_hub.r250213.Expired' />
      </Badge>
    );

  if (isRejected)
    return (
      <Badge type='tint' color='neutral'>
        <FormattedMessage id='Reward_hub.r260219.Rejected' />
      </Badge>
    );

  return null;
};

export const SectionDescriptionTooltipButton = ({ text }: { text: string }) => {
  const overlay = useOverlay();
  return (
    <IconButton
      hierarchy='quaternary'
      size='small'
      onClick={() =>
        overlay.open(({ isOpen, onClose, unmount }) => (
          <SectionDescriptionModal isOpen={isOpen} onClose={onClose} text={text} onExit={unmount} />
        ))
      }
      icon={<InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />}
    />
  );
};

interface ProgressBarProps {
  total: string;
  current: string;
}

export const ProgressBar = ({ total, current }: ProgressBarProps) => {
  const currentValue = Math.min(Number(current), Number(total));

  return (
    <>
      <div className={css({ w: '100%', h: 5, bgColor: 'background.default.level-4', borderRadius: '1rem', mt: 8 })}>
        <div
          className={css({
            maxW: '100%',
            h: '100%',
            bgColor: 'inverse.white',
            borderRadius: '1rem',
            transition: 'width 300ms ease-in-out',
          })}
          style={{ width: `${Num(currentValue).dividedBy(Num(total)).multipliedBy(100).toNumber()}%` }}
        />
      </div>
      <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2', mt: 6 })}>
        <Formatted.Number value={currentValue} precision={UsdtPrecision} toFixed /> /{' '}
        <Formatted.Number value={total} precision={0} toFixed /> {UsdtCurrency}
      </p>
    </>
  );
};

interface ProgressBarItemProps {
  status?: MissionStatus;
  isHide?: boolean;
}

export const ProgressBarItem = ({ status, isHide }: ProgressBarItemProps) => {
  if (isHide || !status) return null;

  const progressItems = status?.progress?.filter(isProgressBarType) ?? [];
  const hasMultiProgress = progressItems.length > 1;

  return (
    <div>
      {progressItems.map((item) => {
        const { target, current } = getProgressParams(item);
        return (
          <div key={item.type} className={css({ flex: 1, '& + &': { mt: 16 } })}>
            {hasMultiProgress && (
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                {item.type === 'DepositAmount' ? (
                  <FormattedMessage id='Reward_hub.r250213.Deposit' />
                ) : (
                  <FormattedMessage id='Reward_hub.r250213.Trade' />
                )}
              </p>
            )}
            <ProgressBar total={target} current={current} />
          </div>
        );
      })}
    </div>
  );
};

export const ExpiredTime = ({
  daysUntilExpiration,
  type = 'regular',
}: {
  daysUntilExpiration: number;
  type?: 'special' | 'regular';
}) => {
  if (daysUntilExpiration <= 0) return null;
  return (
    <p
      className={hstack({
        gap: 4,
        py: 8,
        justify: 'center',
        bgColor: type === 'regular' ? 'background.accent.promotion.tint' : 'background.state.positive.tint',
        textStyle: 'Text/S/Regular',
        color: 'content.default.level-5',
        borderTopRadius: 8,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'transparent',
        borderImageSource:
          type === 'regular'
            ? `linear-gradient(to right, transparent, {colors.border.accent.promotion}, transparent)`
            : `linear-gradient(to right, transparent, {colors.border.state.positive}, transparent)`,
        borderImageSlice: 1,
      })}
    >
      <ClockIcon size={16} className={css({ color: 'content.default.level-5' })} />
      <FormattedMessage id='Reward_hub.r250213.Reward_hub_0213_days_left' values={{ value: daysUntilExpiration }} />
    </p>
  );
};

interface DistributionDateProps {
  date?: string;
  showMaintainMessage?: boolean;
  isHide?: boolean;
}

export const DistributionDate = ({ date, showMaintainMessage, isHide }: DistributionDateProps) => {
  if (isHide || !date) return null;

  return (
    <div className={css({ color: 'content.default.level-1', mt: 4 })}>
      <p className={css({ textStyle: 'Text/M/Regular' })}>
        <FormattedMessage
          id='Reward_hub.r251216.Distributed_by_date'
          values={{ date: <Datetime value={date} format='yyyy.MM.dd, HH:mm' isUTC withUTCText /> }}
        />
      </p>
      {showMaintainMessage && (
        <p className={css({ textStyle: 'Text/S/Regular' })}>
          <FormattedMessage id='Reward_hub.r251216.Maintain_balance_until_distribution' />
        </p>
      )}
    </div>
  );
};

interface MissionTitleProps {
  missionIndex: number;
  sectionIndex: number;
  title?: ReactNode;
  completionReward: CompletionReward;
  status?: MissionStatus;
  isActive?: boolean;
}

export const MissionTitle = ({
  missionIndex,
  sectionIndex,
  title,
  completionReward,
  status,
  isActive,
}: MissionTitleProps) => {
  const hasMultiProgress = status?.progress?.length && status.progress.length > 1;

  return (
    <div
      className={flex({
        justify: 'space-between',
        flexDirection: hasMultiProgress ? 'column' : 'row',
        alignItems: hasMultiProgress ? 'flex-start' : 'center',
      })}
    >
      {title}
      <div className={hstack({ gap: 10, mt: 8, mb: hasMultiProgress ? 16 : 0 })}>
        {status?.progress?.map((progress, i) => (
          <MissionCtaButton
            key={progress.type}
            size='small'
            hierarchy={hasMultiProgress && i === 0 ? 'secondary' : 'primary'}
            missionIndex={missionIndex}
            sectionIndex={sectionIndex}
            completionReward={completionReward}
            missionStatus={status}
            isActive={isActive}
            progress={progress}
            css={{ minW: 66 }}
          />
        ))}
      </div>
    </div>
  );
};

export const SectionDescriptionModal = ({
  text,
  isOpen,
  onClose,
  onExit,
}: {
  text: string;
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}) => {
  // TODO: @gaeun 추후 title과 contents가 분리되어야 함.
  const [title, ...rest] = text.split('\n');
  const description = rest.join('\n');

  return (
    <Modal.Root isOpen={isOpen} mode='bottomSheet' onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={title} />
      </Modal.Header>
      <Modal.Content>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>{description}</p>
      </Modal.Content>
    </Modal.Root>
  );
};
